import { useState, useEffect } from 'react'
import api from '../lib/api'

const DEFAULT_COLOR = '#cccccc'

function emptyRow(kind) {
  const row = { name: '', description: '', color: DEFAULT_COLOR }
  if (kind === 'contact') row.sentimentEnabled = true
  return row
}

function SparkleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.6 4.3L18 9l-4.4 1.7L12 15l-1.6-4.3L6 9l4.4-1.7L12 3z" />
      <path d="M19 14l.7 1.9L21.5 17l-1.8.7L19 19.5l-.7-1.8L16.5 17l1.8-1.1L19 14z" />
    </svg>
  )
}

function CategoryList({ title, items, onChange, ignoreLabel, onSuggest, kind }) {
  const [suggestingIdx, setSuggestingIdx] = useState(null)
  const isContact = kind === 'contact'
  const update = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it))
    onChange(next)
  }
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx))
  const add = () => onChange([...items, emptyRow(kind)])
  const handleSuggest = async (idx) => {
    const it = items[idx]
    if (!it?.name?.trim()) return
    setSuggestingIdx(idx)
    try {
      const description = await onSuggest(it.name)
      if (description) update(idx, { description })
    } finally {
      setSuggestingIdx(null)
    }
  }

  return (
    <div className="categories-block">
      <div className="categories-block-header">
        <h4>{title}</h4>
        <button type="button" className="btn btn-outline" onClick={add}>+ Add</button>
      </div>
      <table className="categories-table">
        <thead>
          <tr>
            <th style={{ width: 160 }}>Name</th>
            <th>Description (helps the AI)</th>
            <th style={{ width: 80 }}>Color</th>
            {isContact && <th style={{ width: 130 }} title="If off, contacts of this type are skipped during 'Analyse All Sentiments'. Per-row Analyse buttons still work.">Bulk sentiment</th>}
            <th style={{ width: 60 }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={idx}>
              <td>
                <input
                  type="text"
                  value={it.name}
                  onChange={(e) => update(idx, { name: e.target.value })}
                  placeholder="e.g. Customer"
                />
              </td>
              <td>
                <div className="categories-desc-cell">
                  <input
                    type="text"
                    value={it.description}
                    onChange={(e) => update(idx, { description: e.target.value })}
                    placeholder="When does the AI choose this?"
                  />
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => handleSuggest(idx)}
                    disabled={!it.name?.trim() || suggestingIdx === idx}
                    title={it.name?.trim() ? 'Generate description with AI' : 'Enter a name first'}
                    aria-label="Generate description with AI"
                  >
                    {suggestingIdx === idx ? '…' : <SparkleIcon />}
                  </button>
                </div>
              </td>
              <td>
                <input
                  type="color"
                  value={it.color || DEFAULT_COLOR}
                  onChange={(e) => update(idx, { color: e.target.value })}
                />
              </td>
              {isContact && (
                <td>
                  <label className="categories-toggle">
                    <input
                      type="checkbox"
                      checked={it.sentimentEnabled !== false}
                      onChange={(e) => update(idx, { sentimentEnabled: e.target.checked })}
                    />
                    <span>{it.sentimentEnabled !== false ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </td>
              )}
              <td>
                <button type="button" className="icon-btn icon-btn-danger" onClick={() => remove(idx)} aria-label="Remove">
                  ×
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={isContact ? 5 : 4} className="empty-state" style={{ padding: 16 }}>No entries — click "+ Add".</td></tr>
          )}
        </tbody>
      </table>
      {ignoreLabel && (
        <div className="categories-ignore-note">
          A built-in <strong>{ignoreLabel}</strong> bucket is always added automatically — emails the AI cannot fit elsewhere are discarded.
        </div>
      )}
    </div>
  )
}

export function CategoriesEditor({ accountId, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    setLoading(true)
    api.get(`/imap-accounts/${accountId}/categories`)
      .then((res) => setData(res.data))
      .catch(() => setMessage({ type: 'error', text: 'Failed to load categories' }))
      .finally(() => setLoading(false))
  }, [accountId])

  const requestSuggestion = async (kind, name) => {
    try {
      const res = await api.post(`/imap-accounts/${accountId}/categories/suggest-description`, {
        name,
        kind,
        businessContext: data?.businessContext || '',
      })
      return res.data.description || ''
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to generate description' })
      return ''
    }
  }

  const [rewriting, setRewriting] = useState(false)
  const handleRewriteContext = async () => {
    const text = (data?.businessContext || '').trim()
    if (!text) {
      setMessage({ type: 'error', text: 'Enter some business context first.' })
      return
    }
    setRewriting(true)
    setMessage(null)
    try {
      const res = await api.post(`/imap-accounts/${accountId}/business-context/rewrite`, { text })
      if (res.data?.text) setData({ ...data, businessContext: res.data.text })
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to rewrite' })
    } finally {
      setRewriting(false)
    }
  }

  const handleSave = async () => {
    if (!data) return
    setSaving(true)
    setMessage(null)
    try {
      const res = await api.put(`/imap-accounts/${accountId}/categories`, {
        businessContext: data.businessContext,
        contactCategories: data.contactCategories,
        sentimentLabels: data.sentimentLabels,
      })
      setData(res.data)
      setMessage({ type: 'success', text: 'Saved.' })
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="loading">Loading categories...</div>
  if (!data) return null

  return (
    <div className="categories-editor">
      {message && <div className={`message ${message.type}`}>{message.text}</div>}

      <div className="form-group">
        <div className="business-context-label-row">
          <label>Business context (used in the AI prompt for this account)</label>
          <button
            type="button"
            className="btn btn-outline business-context-rewrite-btn"
            onClick={handleRewriteContext}
            disabled={rewriting || !data.businessContext?.trim()}
            title="Rewrite the text with AI"
          >
            <SparkleIcon />
            <span>{rewriting ? 'Rewriting...' : 'Rewrite with AI'}</span>
          </button>
        </div>
        <textarea
          rows={2}
          value={data.businessContext}
          onChange={(e) => setData({ ...data, businessContext: e.target.value })}
          placeholder="e.g. an online luxury furniture shop based in the UK"
        />
      </div>

      <CategoryList
        title="Contact categories"
        items={data.contactCategories}
        onChange={(items) => setData({ ...data, contactCategories: items })}
        ignoreLabel={data.ignoreLabel}
        onSuggest={(name) => requestSuggestion('contact', name)}
        kind="contact"
      />

      <CategoryList
        title="Sentiment labels"
        items={data.sentimentLabels}
        onChange={(items) => setData({ ...data, sentimentLabels: items })}
        onSuggest={(name) => requestSuggestion('sentiment', name)}
        kind="sentiment"
      />

      <div className="categories-actions">
        <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
